/**
 * PrintTemplateSheet.tsx — a print template drawn with sample data, as corp's
 * print-template-preview does: each section as it prints (renderPrintSection,
 * also used by the builder's cards), the whole sheet (PrintTemplateSheet) and
 * the Preview dialog with its Quote / Receipt / Membership Agreement switch.
 *
 * The figures are fixed and obviously fake: the preview shows the LAYOUT —
 * which lines print, in what order — not real numbers.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  PRINT_DOC_LABEL,
  PRINT_SECTION_BY_KEY,
  offeredDocTypes,
  type PrintDocType,
  type PrintFlags,
  type PrintOrderField,
} from '@/services/printTemplatesAdmin.service';

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const SAMPLE = {
  patient: 'Jane Sample',
  dob: '03/22/1984',
  procedureDate: '10/14/2026',
  quoteDate: '09/07/2026',
  location: 'Main Clinic — Suite 200',
  planId: '10482',
  doctor: 'Dr. Alex Surgeon',
  coordinator: 'Sam Coordinator',
  procedures: [
    { category: 'Body Contouring', specific: 'Abdominoplasty (Full)', description: 'Full tummy tuck with muscle repair', standard: 8500, effective: 8000 },
    { category: 'Liposuction', specific: 'Liposuction — Flanks', description: 'Flank liposuction, bilateral', standard: 3200, effective: 3000 },
  ],
  services: [
    { name: 'Post-Op Garment', fee: 150 },
    { name: 'Lymphatic Massage (3)', fee: 450 },
  ],
  anesthesia: 1800,
  operatingRoom: 2400,
  items: [
    { name: 'Scar Gel 30ml', description: 'Silicone scar treatment', pre: 60, tax: 4.5, post: 64.5 },
    { name: 'Compression Wrap', description: 'Stage 2 compression', pre: 85, tax: 6.38, post: 91.38 },
  ],
  memberships: [{ name: 'Glow Membership', description: 'Monthly facial + 10% off products', fee: 149 }],
  packages: [{ name: 'Laser Package (6)', description: 'Six laser hair removal sessions', fee: 1500 }],
  discounts: [
    { name: 'Surgeon Courtesy', amount: 500, appliesToTotal: false },
    { name: 'Fall Promotion', amount: 300, appliesToTotal: true },
  ],
  payment: { type: 'Payment', method: 'Visa ****4242', ref: 'TXN-88123', amount: 2000, notes: 'Deposit at consult', date: '09/07/2026' },
  credit: { type: 'Gift Card', ref: 'GC-5521', value: 500, redeemed: 200, balance: 300, notes: 'Birthday gift card', date: '08/01/2026' },
};

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-1.5 mt-5 border-b border-slate-800 pb-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-800">
      {children}
    </h3>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1 text-[11px]">
      <span className="text-slate-500">{label}:</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function KvGrid({ rows, className }: { rows: [boolean, string, string][]; className?: string }) {
  const shown = rows.filter((r) => r[0]);
  if (shown.length === 0) return null;
  return (
    <div className={cn('grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2', className)}>
      {shown.map(([, l, v]) => (
        <Kv key={l} label={l} value={v} />
      ))}
    </div>
  );
}

function TotalLine({ label, value, strong, negative }: { label: string; value: number; strong?: boolean; negative?: boolean }) {
  return (
    <div className={cn('flex justify-between text-[11px]', strong ? 'mt-1 border-t border-slate-300 pt-1 font-bold text-slate-900' : 'text-slate-700')}>
      <span>{label}</span>
      <span>{negative ? `− ${money(value)}` : money(value)}</span>
    </div>
  );
}

const RIGHT = /Fee|Tax|Amount/;

function SheetTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="border-b border-slate-300 text-left text-slate-500">
          {columns.map((c, i) => (
            <th key={c} className={cn('py-0.5 font-medium', i > 0 && RIGHT.test(c) && 'text-right')}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-b border-slate-100">
            {r.map((cell, ci) => (
              <td key={ci} className={cn('py-0.5 text-slate-800', ci > 0 && RIGHT.test(columns[ci]) && 'text-right')}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return <p className="text-[11px] italic text-slate-500">{children}</p>;
}

function Totals({ children }: { children: ReactNode }) {
  return <div className="ml-auto mt-2 w-64">{children}</div>;
}

/** An itemized block: optional heading, then the chosen columns, or a "heading only" note. */
function Itemized({ heading, title, columns, rows, empty }: { heading: boolean; title: string; columns: string[]; rows: string[][]; empty: string }) {
  if (!heading && columns.length === 0) return null;
  return (
    <>
      {heading && <SectionTitle>{title}</SectionTitle>}
      {columns.length > 0 ? <SheetTable columns={columns} rows={rows} /> : <Placeholder>{empty}</Placeholder>}
    </>
  );
}

/** Picks the cells of the shown columns, in column order. */
function pick(cells: [boolean, string, string][]): { columns: string[]; row: string[] } {
  const shown = cells.filter((c) => c[0]);
  return { columns: shown.map((c) => c[1]), row: shown.map((c) => c[2]) };
}

/**
 * One section with sample data, or null when it would print nothing (its
 * Display switch is off, nothing in it is on, or it is receipts-only and the
 * document is not a receipt).
 */
export function renderPrintSection(
  key: PrintOrderField,
  f: PrintFlags,
  discountDisplayType: number,
  disclosureName: string,
  docType: PrintDocType,
): ReactNode {
  const section = PRINT_SECTION_BY_KEY[key];
  if (section.displayKey && !f[section.displayKey]) return null;
  const S = SAMPLE;

  switch (key) {
    case 'orderApptDetails':
      return (
        <KvGrid
          className="mt-3"
          rows={[
            [f.patientName, 'Patient Name', S.patient],
            [f.apptDate, 'Procedure Date', S.procedureDate],
            [f.birthDateLine, 'Birth Date', S.dob],
            [f.apptLocation, 'Location', S.location],
            [f.plan, 'Plan ID', S.planId],
            [f.doctorName, 'Doctor', S.doctor],
            [f.quoteDate, 'Quote Date', S.quoteDate],
            [f.coordName, 'Coordinator', S.coordinator],
          ]}
        />
      );

    case 'orderProceduresLine': {
      const cells = (p: (typeof S.procedures)[number]): [boolean, string, string][] => [
        [f.procNames, 'Procedure', p.category],
        [f.procSpecific, 'Specific', p.specific],
        [f.procDescriptions, 'Description', p.description],
        [f.standardFees, 'Standard Fee', money(p.standard)],
        [f.effectiveFees, 'Effective Fee', money(p.effective)],
      ];
      return (
        <Itemized
          heading={f.procHeader}
          title="Procedures"
          columns={pick(cells(S.procedures[0])).columns}
          rows={S.procedures.map((p) => pick(cells(p)).row)}
          empty="Heading only — no procedure columns selected"
        />
      );
    }

    case 'orderProceduresTotals':
      if (!(f.procStandardFeeTotal || f.procDiscountTotal || f.procDiscounts || f.procPostDiscountTotal)) return null;
      return (
        <Totals>
          {f.procStandardFeeTotal && <TotalLine label="Procedures (pre-discount)" value={11700} />}
          {f.procDiscountTotal && <TotalLine label="Standard fee discount" value={700} negative />}
          {f.procDiscounts && <TotalLine label="Applied discounts" value={500} negative />}
          {f.procPostDiscountTotal && <TotalLine label="Procedures total" value={10500} strong />}
        </Totals>
      );

    case 'orderServiceServicesLine': {
      if (!(f.serviceHeader || f.anesthesiaFees || f.operatingRoomFees || f.serviceNames)) return null;
      const lines: [string, number][] = [];
      if (f.anesthesiaFees) lines.push(['Anesthesia', S.anesthesia]);
      if (f.operatingRoomFees) lines.push(['Operating Room', S.operatingRoom]);
      if (f.serviceNames) for (const s of S.services) lines.push([s.name, s.fee]);
      return (
        <>
          {f.serviceHeader && <SectionTitle>Surgery Services</SectionTitle>}
          {lines.length > 0 ? (
            <SheetTable
              columns={f.serviceFees ? ['Service', 'Fee'] : ['Service']}
              rows={lines.map(([n, fee]) => (f.serviceFees ? [n, money(fee)] : [n]))}
            />
          ) : (
            <Placeholder>Heading only — no service lines selected</Placeholder>
          )}
        </>
      );
    }

    case 'orderServiceServicesTotals':
      if (!(f.serviceFeeTotal || f.serviceDiscounts || f.servicePostDiscountTotal)) return null;
      return (
        <Totals>
          {f.serviceFeeTotal && <TotalLine label="Services (pre-discount)" value={4800} />}
          {f.serviceDiscounts && <TotalLine label="Applied discounts" value={100} negative />}
          {f.servicePostDiscountTotal && <TotalLine label="Services total" value={4700} strong />}
        </Totals>
      );

    case 'orderMaterialsLine': {
      const cells = (p: (typeof S.items)[number]): [boolean, string, string][] => [
        [f.itemLineItems, 'Product', p.name],
        [f.itemDescriptions, 'Description', p.description],
        [f.itemSubFees, 'Pre-Tax Fee', money(p.pre)],
        [f.itemSalesTax, 'Sales Tax', money(p.tax)],
        [f.itemFees, 'Post-Tax Fee', money(p.post)],
      ];
      return (
        <Itemized
          heading={f.itemHeader}
          title="Products"
          columns={pick(cells(S.items[0])).columns}
          rows={S.items.map((p) => pick(cells(p)).row)}
          empty="Heading only — no product columns selected"
        />
      );
    }

    case 'orderMaterialsTotals':
      if (!(f.itemTotal || f.itemDiscounts || f.itemPostDiscountTotal)) return null;
      return (
        <Totals>
          {f.itemTotal && <TotalLine label="Products (pre-discount)" value={155.88} />}
          {f.itemDiscounts && <TotalLine label="Applied discounts" value={15} negative />}
          {f.itemPostDiscountTotal && <TotalLine label="Products total" value={140.88} strong />}
        </Totals>
      );

    case 'orderMembershipsLine': {
      const cells = (m: (typeof S.memberships)[number]): [boolean, string, string][] => [
        [f.membershipName, 'Membership', m.name],
        [f.membershipDescription, 'Description', m.description],
        [f.membershipFee, 'Fee', `${money(m.fee)} / mo`],
      ];
      return (
        <Itemized
          heading={f.membershipHeader}
          title="Memberships"
          columns={pick(cells(S.memberships[0])).columns}
          rows={S.memberships.map((m) => pick(cells(m)).row)}
          empty="Heading only — no membership columns selected"
        />
      );
    }

    case 'orderMembershipsTotals':
      if (!(f.membershipSubTotal || f.membershipDiscounts || f.membershipTotal)) return null;
      return (
        <Totals>
          {f.membershipSubTotal && <TotalLine label="Memberships (pre-discount)" value={149} />}
          {f.membershipDiscounts && <TotalLine label="Applied discounts" value={0} negative />}
          {f.membershipTotal && <TotalLine label="Memberships total" value={149} strong />}
        </Totals>
      );

    case 'orderPackagesLine': {
      const cells = (p: (typeof S.packages)[number]): [boolean, string, string][] => [
        [f.packageName, 'Package', p.name],
        [f.packageDescription, 'Description', p.description],
        [f.packageFee, 'Fee', money(p.fee)],
      ];
      return (
        <Itemized
          heading={f.packageHeader}
          title="Packages"
          columns={pick(cells(S.packages[0])).columns}
          rows={S.packages.map((p) => pick(cells(p)).row)}
          empty="Heading only — no package columns selected"
        />
      );
    }

    case 'orderPackagesTotals':
      if (!(f.packageSubTotal || f.packageDiscounts || f.packageTotal)) return null;
      return (
        <Totals>
          {f.packageSubTotal && <TotalLine label="Packages (pre-discount)" value={1500} />}
          {f.packageDiscounts && <TotalLine label="Applied discounts" value={150} negative />}
          {f.packageTotal && <TotalLine label="Packages total" value={1350} strong />}
        </Totals>
      );

    case 'orderDiscountsLine': {
      if (!f.discountHeader && !f.discountLineItems && !f.discountAmounts) return null;
      // 1 = only the discounts applied to the whole quote; 0 = every discount.
      const discounts = discountDisplayType === 1 ? S.discounts.filter((d) => d.appliesToTotal) : S.discounts;
      const cells = (d: (typeof S.discounts)[number]): [boolean, string, string][] => [
        [f.discountLineItems, 'Discount', d.name],
        [f.discountAmounts, 'Amount', `− ${money(d.amount)}`],
      ];
      const columns = pick(cells(S.discounts[0])).columns;
      return (
        <>
          <Itemized
            heading={f.discountHeader}
            title="Applied Discounts"
            columns={columns}
            rows={discounts.map((d) => pick(cells(d)).row)}
            empty="Heading only — no discount columns selected"
          />
          {columns.length > 0 && (
            <p className="mt-0.5 text-[10px] text-slate-400">
              {discountDisplayType === 1 ? 'Showing only discounts applied to the total' : 'Showing all discounts'}
            </p>
          )}
        </>
      );
    }

    case 'orderDiscountsTotals':
      if (!f.discountTotal) return null;
      return (
        <Totals>
          <TotalLine label="Total discounts" value={800} negative strong />
        </Totals>
      );

    case 'orderFinancing':
      return (
        <>
          <SectionTitle>Financing Options</SectionTitle>
          <Placeholder>Financing options and monthly payment estimates print here.</Placeholder>
        </>
      );

    case 'orderTotal':
      if (!(f.depositLine || f.additionalPayments || f.balanceDue || f.grandTotal)) return null;
      return (
        <div className="ml-auto mt-3 w-64 rounded border border-slate-300 p-2">
          {f.grandTotal && <TotalLine label={`${PRINT_DOC_LABEL[docType]} total`} value={16690.88} strong />}
          {f.depositLine && <TotalLine label="Deposit" value={2000} negative />}
          {f.additionalPayments && <TotalLine label="Additional payments" value={500} negative />}
          {f.balanceDue && <TotalLine label="Balance due" value={14190.88} strong />}
        </div>
      );

    case 'orderPayment':
      if (docType !== 'receipt') return null;
      if (!(f.paymentType || f.paymentMethod || f.paymentRefNumber || f.paymentAmount || f.paymentNotes || f.paymentDate)) return null;
      return (
        <>
          <SectionTitle>Payment</SectionTitle>
          <KvGrid
            rows={[
              [f.paymentType, 'Type', S.payment.type],
              [f.paymentMethod, 'Method', S.payment.method],
              [f.paymentRefNumber, 'Reference Number', S.payment.ref],
              [f.paymentAmount, 'Amount', money(S.payment.amount)],
              [f.paymentNotes, 'Notes', S.payment.notes],
              [f.paymentDate, 'Date', S.payment.date],
            ]}
          />
        </>
      );

    case 'orderCredit': {
      if (docType !== 'receipt') return null;
      if (!(f.creditType || f.creditRefNumber || f.creditAmount || f.creditRedeemedAmount || f.creditBalance || f.creditNotes || f.creditDate)) {
        return null;
      }
      return (
        <>
          <SectionTitle>Payment Credit</SectionTitle>
          <KvGrid
            rows={[
              [f.creditType, 'Type', S.credit.type],
              [f.creditRefNumber, 'Reference Number', S.credit.ref],
              [f.creditAmount, 'Credit Value', money(S.credit.value)],
              [f.creditRedeemedAmount, 'Redeemed Amount', money(S.credit.redeemed)],
              [f.creditBalance, 'Remaining Balance', money(S.credit.balance)],
              [f.creditNotes, 'Credit Notes', S.credit.notes],
              [f.creditDate, 'Purchase Date', S.credit.date],
            ]}
          />
        </>
      );
    }

    case 'orderDisclosures':
      return (
        <>
          <SectionTitle>Disclosures{disclosureName ? ` — ${disclosureName}` : ''}</SectionTitle>
          {disclosureName ? (
            <p className="text-[10px] leading-snug text-slate-600">
              The text of the &ldquo;{disclosureName}&rdquo; disclosure prints here. Fees quoted are estimates based on the procedures
              discussed and may change if the treatment plan changes. This document is not a contract.
            </p>
          ) : (
            <Placeholder>Displayed, but no disclosure selected — nothing will print here.</Placeholder>
          )}
        </>
      );

    case 'orderSignature':
      return (
        <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <div className="h-6 border-b border-slate-800" />
            <p className="mt-0.5 text-[10px] text-slate-500">Patient Signature / Date</p>
          </div>
          {f.witnessSignature && (
            <div>
              <div className="h-6 border-b border-slate-800" />
              <p className="mt-0.5 text-[10px] text-slate-500">Witness Signature / Date</p>
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
}

export interface PrintSheetInput {
  name: string;
  flags: PrintFlags;
  sequence: PrintOrderField[];
  discountDisplayType: number;
  disclosureName: string;
}

/** The whole printed page with sample data. */
export function PrintTemplateSheet({ input, docType }: { input: PrintSheetInput; docType: PrintDocType }) {
  const f = input.flags;
  return (
    <div className="mx-auto w-full max-w-[720px] rounded bg-white p-8 text-slate-900 shadow-md ring-1 ring-slate-200">
      <div className="flex items-start justify-between gap-4 border-b-2 border-slate-800 pb-3">
        <div>
          {f.logoDisplay && (
            <div className="mb-2 flex h-12 w-36 items-center justify-center rounded border border-slate-300 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
              Account logo
            </div>
          )}
          <p className="text-sm font-bold">Practice Name</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold uppercase tracking-wide">{PRINT_DOC_LABEL[docType]}</p>
          <p className="text-[10px] text-slate-500">{input.name || 'Untitled template'}</p>
        </div>
      </div>
      {input.sequence.map((key) => {
        const node = renderPrintSection(key, f, input.discountDisplayType, input.disclosureName, docType);
        return node ? <div key={key}>{node}</div> : null;
      })}
    </div>
  );
}

/** The Preview dialog: the sheet, with a switch between the documents the template is offered for. */
export function PrintTemplatePreviewModal({ input, onClose }: { input: PrintSheetInput; onClose: () => void }) {
  const offered = useMemo(() => offeredDocTypes(input.flags), [input.flags]);
  const [docType, setDocType] = useState<PrintDocType>(offered[0]);
  const shown = offered.includes(docType) ? docType : offered[0];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      bodyClassName="bg-slate-100 py-6"
      title={
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base font-semibold text-slate-900">Preview — {input.name || 'Untitled template'}</h2>
            {offered.length > 1 && (
              <div className="flex items-center gap-1" role="group" aria-label="Document">
                {offered.map((d) => (
                  <Button key={d} size="xs" variant={shown === d ? 'primary' : 'secondary'} aria-pressed={shown === d} onClick={() => setDocType(d)}>
                    {PRINT_DOC_LABEL[d]}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-500">
            Sample data, real layout — shows which lines print and in what order. Payment and credit blocks only print on receipts.
          </p>
        </div>
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <PrintTemplateSheet input={input} docType={shown} />
    </Modal>
  );
}
